/*
 * features (config)
 */
import {
	type LucideIcon,
	Bot,
	Brain,
	Clipboard,
	GitPullRequest,
	History,
	Lightbulb,
	Music,
	PocketKnife,
	ScrollText,
	Tags,
	Users,
} from "lucide-react"
import type { ComponentType } from "react"
import { ContextSuggestionsPendingBadge } from "../components/badges/context-suggestions-pending-badge"
import { JournalTodayBadge } from "../components/badges/journal-today-badge"
import { ReflectionsPendingBadge } from "../components/badges/reflections-pending-badge"

export type FeatureContext = {
	devMode?: boolean
}

export type Feature = {
	key: string
	to: string
	icon: LucideIcon
	titleKey: string
	descriptionKey: string
	gradient: string
	enabled?: boolean | ((ctx: FeatureContext) => boolean)
	/**
	 * Optional self-fetching badge (e.g. a "pending" count) rendered next to
	 * the feature's label in the Sidebar and Dashboard. Kept as a component
	 * reference (not a hook) so it can be rendered conditionally without
	 * violating the Rules of Hooks — each mounted badge calls its own query.
	 */
	Badge?: ComponentType
}

/*
 * The single source of truth for every "core feature" surfaced in the
 * Sidebar's Services group and on the Dashboard. `enabled` accepts either a
 * static boolean or a predicate (e.g. `({ devMode }) => devMode`) so a
 * feature can be gated to dev/test environments before it's ready for prod.
 */
export const FEATURES: Feature[] = [
	{
		key: "journal",
		to: "/journal",
		icon: ScrollText,
		titleKey: "features.journal.title",
		descriptionKey: "features.journal.description",
		gradient: "from-emerald-500 to-emerald-300",
		Badge: JournalTodayBadge,
	},
	{
		key: "chat",
		to: "/chat",
		icon: Bot,
		titleKey: "features.chat.title",
		descriptionKey: "features.chat.description",
		gradient: "from-blue-500 to-blue-300",
	},
	{
		key: "reflections",
		to: "/reflections",
		icon: Lightbulb,
		titleKey: "features.reflections.title",
		descriptionKey: "features.reflections.description",
		gradient: "from-amber-500 to-amber-300",
		Badge: ReflectionsPendingBadge,
	},
	{
		key: "quizzes",
		to: "/quiz",
		icon: PocketKnife,
		titleKey: "features.quizzes.title",
		descriptionKey: "features.quizzes.description",
		gradient: "from-purple-500 to-purple-300",
	},
	{
		key: "therapySessions",
		to: "/therapy/session",
		icon: Clipboard,
		titleKey: "features.therapySessions.title",
		descriptionKey: "features.therapySessions.description",
		gradient: "from-emerald-500 to-emerald-300",
	},
	{
		key: "traits",
		to: "/traits",
		icon: Tags,
		titleKey: "features.traits.title",
		descriptionKey: "features.traits.description",
		gradient: "from-pink-500 to-pink-300",
	},
	{
		key: "people",
		to: "/people",
		icon: Users,
		titleKey: "features.people.title",
		descriptionKey: "features.people.description",
		gradient: "from-teal-500 to-teal-300",
	},
	{
		key: "songs",
		to: "/songs",
		icon: Music,
		titleKey: "features.songs.title",
		descriptionKey: "features.songs.description",
		gradient: "from-violet-500 to-violet-300",
	},
	{
		key: "timelineEvents",
		to: "/timeline-events",
		icon: History,
		titleKey: "features.timelineEvents.title",
		descriptionKey: "features.timelineEvents.description",
		gradient: "from-orange-500 to-orange-300",
	},
	{
		key: "psychologicalContext",
		to: "/psychological-context",
		icon: Brain,
		titleKey: "features.psychologicalContext.title",
		descriptionKey: "features.psychologicalContext.description",
		gradient: "from-indigo-500 to-indigo-300",
	},
	{
		key: "contextSuggestions",
		to: "/context-suggestions",
		icon: GitPullRequest,
		titleKey: "features.contextSuggestions.title",
		descriptionKey: "features.contextSuggestions.description",
		gradient: "from-rose-500 to-rose-300",
		Badge: ContextSuggestionsPendingBadge,
	},
]

export function getEnabledFeatures(ctx: FeatureContext = {}): Feature[] {
	return FEATURES.filter((feature) => {
		if (typeof feature.enabled === "function") {
			return feature.enabled(ctx)
		}
		if (typeof feature.enabled === "boolean") {
			return feature.enabled
		}
		return true
	})
}
